import React, { useMemo } from 'react';
import { useWindowDimensions } from 'react-native';
import { DNABox, DNAButton, DNAText, DNAIcon, Iffy, DNAButtonGroup } from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import { useAppSettings } from 'src/state/context/AppSettings';
import TitleBarEllipsisMenu from './TitleBarEllipsisMenu';
import { TitleBarComponent } from './TitleBar.types';

const S = {
  titleBarContainer: {
    paddingTop: 16,
    paddingBottom: 24,
    backgroundColor: colors['color-white'],
  },
  titleBarContainerTablet: {
    paddingTop: 16,
    paddingBottom: 24,
    backgroundColor: colors['color-white'],
  },
  title: {
    color: colors['color-ui2-neutral-800'],
    maxWidth: '60vw',
  },
  ctaButtonsContainer: {
    marginRight: 8,
  },
  toggleViewButtonGroup: {
    borderWidth: 1,
    borderColor: colors['color-ui2-neutral-200'],
    borderRadius: 6,
  },
};

// Compound Component Sub-components
const TitleBarButton: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  return children as React.ReactElement;
};

const TitleBarToggleView: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  return children as React.ReactElement;
};

// Set display names for compound component identification
TitleBarButton.displayName = 'TitleBarButton';
TitleBarToggleView.displayName = 'TitleBarToggleView';

const TitleBar: TitleBarComponent = (props) => {
  const { deviceMode } = useAppSettings();
  const { width } = useWindowDimensions();
  const isTablet = deviceMode === 'tablet';
  const isNarrow = width < 1150;

  const containerStyle = useMemo(() => {
    if (isTablet) return S.titleBarContainerTablet;
    return S.titleBarContainer;
  }, [isTablet]);

  // Extract compound components from children
  const compoundComponents = useMemo(() => {
    const buttons = React.Children.toArray(props.children).filter(
      child => React.isValidElement(child) &&
        typeof child.type === 'function' &&
        (child.type as any)?.displayName === TitleBarButton.displayName,
    ) as React.ReactElement[];

    const toggleView = React.Children.toArray(props.children).find(
      child => React.isValidElement(child) &&
        typeof child.type === 'function' &&
        (child.type as any)?.displayName === TitleBarToggleView.displayName,
    ) as React.ReactElement | undefined;

    return {
      buttons,
      toggleView,
    };
  }, [props.children]);

  // Determine if we have CTA buttons
  const hasCTAButtons = compoundComponents.buttons.length > 0;

  const hasRightSection = hasCTAButtons || props.showEllipsis || props.showToggleView || compoundComponents.toggleView;
  const { activeView, onToggle } = props.toggleView || {};

  const isListMode = activeView === 'list'

  return (
    <DNABox fill style={containerStyle} testID={props.testID}>
      <DNABox
        fill
        spacing={!isNarrow ? 'between' : 'sm'}
        appearance={isNarrow ? 'col' : 'row'}
        childStyle={[0, { flex: 2 }]}
      >
        {/* LEFT SECTION */}
        <DNABox appearance="col" spacing="xs">
          {/* Badge */}
          <Iffy is={props.badge}>
            <DNABox>
              {props.badge}
            </DNABox>
          </Iffy>
          {/* Title */}
          <DNABox>
            <DNAText
              h3
              bold
              style={S.title}
            >
              {props.title}
            </DNAText>
          </DNABox>
          {/* Description */}
          <DNABox>
            <Iffy is={props.description}>
              <DNAText
                status={props.descriptionStatus ?? 'tertiary'}
              >
                {props.description}
              </DNAText>
            </Iffy>
          </DNABox>
        </DNABox>

        {/* RIGHT SECTION */}
        <Iffy is={hasRightSection}>
          <DNABox alignX={isNarrow ? 'start' : 'center'} fill alignY="center">
            {/* CTA Buttons */}
            <Iffy is={hasCTAButtons}>
              <DNABox appearance="row" spacing="sm" style={S.ctaButtonsContainer}>
                {compoundComponents.buttons.map((button, index) => (
                  <React.Fragment key={index}>
                    {button}
                  </React.Fragment>
                ))}
              </DNABox>
            </Iffy>

            {/* Ellipsis Button */}
            <Iffy is={props.showEllipsis && props.ellipsisMenu}>
              <TitleBarEllipsisMenu ellipsisMenu={props.ellipsisMenu!} />
            </Iffy>

            {/* Toggle View Buttons - Compound Component or Props */}
            <Iffy is={compoundComponents.toggleView || props.showToggleView}>
              {compoundComponents.toggleView || (
                <DNAButtonGroup style={S.toggleViewButtonGroup}>
                  <DNAButton
                    appearance={isListMode ? 'filled' : 'ghost'}
                    status={isListMode ? 'transparent' : 'subtle'}
                    size={isTablet ? 'xl' : 'md'}
                    // [NOTE] - THESE ARE ONE-OFF STYLES -- DISCOURAGE USAGE LIKE THIS
                    //          AND WORK WITH DESIGN TO ADD A PROPER, REUSABLE SOLUTION
                    accessoryLeft={() => (
                      <DNAIcon.Styled
                        name="rows-fill"
                        size={isTablet ? 'xl' : 'md'}
                        color={
                          !isListMode
                            ? colors['color-ui2-neutral-300']
                            : colors['color-white']
                        }
                      />
                    )}
                    onPress={() => onToggle?.('list')}
                    style={{ backgroundColor: isListMode ? colors['color-ui2-neutral-300'] : 'transparent' }}
                  />
                  <DNAButton
                    appearance={!isListMode ? 'filled' : 'ghost'}
                    status={!isListMode ? 'transparent' : 'subtle'}
                    size={isTablet ? 'xl' : 'md'}
                    accessoryLeft={() => (
                      <DNAIcon.Styled
                        name="grid-four-fill"
                        size={isTablet ? 'xl' : 'md'}
                        color={
                          isListMode
                            ? colors['color-ui2-neutral-300']
                            : colors['color-white']
                        }
                      />
                    )}
                    onPress={() => { onToggle?.('grid') }}
                    style={{ backgroundColor: !isListMode ? colors['color-ui2-neutral-300'] : 'transparent' }}
                  />
                </DNAButtonGroup>
              )}
            </Iffy>
          </DNABox>
        </Iffy>
      </DNABox>
    </DNABox>
  );
};

// Attach compound components to TitleBar
TitleBar.Button = TitleBarButton;
TitleBar.ToggleView = TitleBarToggleView;

export default TitleBar;
